import {
  Component, Input, OnChanges, OnDestroy, SimpleChange, SimpleChanges, ViewChild, ViewContainerRef,
  Injector, ComponentRef, EventEmitter, createNgModule, Type
} from '@angular/core';
import { Subscription } from 'rxjs';

type OutputHandler = (event: any) => void;

/**
 * LazyHeavyComponent
 * ----------------------------------------------------------------
 * Renderiza dinamicamente un wrapper "pesado" cargando el
 * UiHeavyModule via import() dinamico la primera vez que se invoca.
 *
 * Uso (solo inputs):
 *   <app-lazy-heavy
 *      type="gantt"
 *      [inputs]="{ wsUrl: '...' }">
 *   </app-lazy-heavy>
 *
 * Uso (inputs + outputs):
 *   <app-lazy-heavy
 *      type="calendario"
 *      [inputs]="{ tableData2: data }"
 *      [outputs]="{ parentAbrirURL: onAbrirURL.bind(this) }">
 *   </app-lazy-heavy>
 *
 * Webpack emitira un chunk separado para UiHeavyModule. Mientras nadie
 * use <app-lazy-heavy>, ese chunk NO se descarga.
 *
 * Compat: requiere Angular 14.1+ por createNgModule + viewContainerRef
 *         .createComponent(Type, { ngModuleRef }).
 */
@Component({
  selector: 'app-lazy-heavy',
  template: `
    <ng-container #vc></ng-container>
    <div *ngIf="cargando" class="text-center py-3 text-muted small">
      <i class="fa fa-spinner fa-spin me-1"></i> Cargando componente...
    </div>
    <div *ngIf="error" class="alert alert-danger small m-2">
      No se pudo cargar el componente <b>{{ type }}</b>: {{ error }}
    </div>
  `
})
export class LazyHeavyComponent implements OnChanges, OnDestroy {

  /** Tipo de componente pesado a renderizar (clave del registry de UiHeavyModule). */
  @Input() type: string = '';

  /** Inputs a pasarle al componente hijo una vez instanciado. */
  @Input() inputs: { [key: string]: any } = {};

  /**
   * Handlers a suscribir a los EventEmitter del componente hijo.
   * Las claves deben coincidir con el nombre del @Output() del hijo.
   * Ejemplo:
   *   [outputs]="{ parentAbrirURL: fnQueRecibeElEvento }"
   */
  @Input() outputs: { [key: string]: OutputHandler } = {};

  @ViewChild('vc', { read: ViewContainerRef, static: true })
  private vc!: ViewContainerRef;

  cargando = false;
  error = '';

  private compRef?: ComponentRef<any>;
  private lastLoadedType = '';
  private outputSubs: Subscription[] = [];

  /** Inputs ya aplicados al menos una vez, para calcular firstChange por clave. */
  private inputsVistos = new Set<string>();

  constructor(private injector: Injector) { }


  async ngOnChanges(changes: SimpleChanges) {
    if (changes['type'] && this.type && this.type !== this.lastLoadedType) {
      await this.cargarComponente(this.type);
    } else if (this.compRef) {
      if (changes['inputs'])  this.aplicarInputs();
      if (changes['outputs']) this.aplicarOutputs();
    }
  }


  ngOnDestroy(): void {
    this.limpiarOutputs();
    this.compRef?.destroy();
    this.compRef = undefined;
  }


  private async cargarComponente(type: string) {
    this.cargando = true;
    this.error = '';
    this.limpiarOutputs();
    this.vc.clear();
    this.compRef = undefined;
    this.inputsVistos.clear();

    try {
      const mod = await import(
        /* webpackChunkName: "ui-heavy" */
        './ui-heavy.module'
      );
      const moduleRef = createNgModule(mod.UiHeavyModule, this.injector);
      const Cmp: Type<any> | null = mod.UiHeavyModule.getComponent(type);

      if (!Cmp) {
        this.error = 'Tipo "' + type + '" no esta registrado en UiHeavyModule.';
        return;
      }

      this.compRef = this.vc.createComponent(Cmp, {
        ngModuleRef: moduleRef
      });
      this.aplicarInputs();
      this.aplicarOutputs();
      // Primer render garantizado (dispara ngOnInit) aunque el componente no
      // reciba inputs (ej. type="archivos" sin [inputs]).
      try { this.compRef.changeDetectorRef.detectChanges(); } catch { }
      this.lastLoadedType = type;
    } catch (e: any) {
      this.error = e?.message || String(e);
    } finally {
      this.cargando = false;
    }
  }


  private aplicarInputs() {
    if (!this.compRef || !this.inputs) return;
    const instance: any = this.compRef.instance;
    const changes: SimpleChanges = {};
    let huboCambios = false;

    // Solo se aplican (y notifican) los inputs cuyo valor cambio por referencia,
    // igual que hace el binding de template de Angular. Esto evita reprocesar en
    // cada ciclo de CD cuando el padre pasa [inputs]="{...}" como objeto literal.
    Object.keys(this.inputs).forEach(k => {
      const previousValue = instance[k];
      const currentValue = this.inputs[k];
      if (previousValue === currentValue) return;
      try {
        instance[k] = currentValue;
        const firstChange = !this.inputsVistos.has(k);
        this.inputsVistos.add(k);
        changes[k] = new SimpleChange(previousValue, currentValue, firstChange);
        huboCambios = true;
      } catch { /* ignorar inputs que no existan */ }
    });

    if (!huboCambios) return;

    // Los componentes creados dinamicamente con inputs imperativos NO reciben
    // ngOnChanges de Angular. Lo invocamos a mano para replicar el binding de
    // template: asi los componentes que reaccionan en ngOnChanges (ej.
    // CalendarioComponent, que reconstruye eventSettings) se actualizan cuando
    // cambian sus datos.
    if (typeof instance.ngOnChanges === 'function') {
      try { instance.ngOnChanges(changes); }
      catch (e) { console.warn('[LazyHeavy] error en ngOnChanges de "' + this.type + '"', e); }
    }

    try { this.compRef.changeDetectorRef.detectChanges(); } catch { }
  }


  private aplicarOutputs() {
    if (!this.compRef || !this.outputs) return;
    this.limpiarOutputs();
    const instance: any = this.compRef.instance;
    Object.keys(this.outputs).forEach(key => {
      try {
        const emitter = instance[key];
        if (emitter && emitter instanceof EventEmitter) {
          const handler = this.outputs[key];
          this.outputSubs.push(emitter.subscribe((ev: any) => {
            try { handler(ev); } catch { /* swallow */ }
          }));
        }
      } catch { /* ignorar outputs que no existan */ }
    });
  }


  private limpiarOutputs() {
    this.outputSubs.forEach(s => { try { s.unsubscribe(); } catch { } });
    this.outputSubs = [];
  }
}
